import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil'
import { FC, useEffect, useMemo } from 'react'
import { GeoJSON2SVG } from 'geojson2svg'
import { HeaderCover } from 'src/v2/components/sidebar-header-cover'
import { Colors, Shapes } from 'src/components/theme'
import { MapSquareIcon } from 'src/components/icons/map-square'
import bbox from '@turf/bbox'
import styled from '@emotion/styled'
import { SidebarHeaderContent } from 'src/v2/components/sidebar-header-content'
import { SidebarHeader } from 'src/v2/components/sidebar-header'
import { formatString } from 'src/utils/format-string'
import { UserListsPopover } from 'src/components/user-lists-popover'
import { useAtomValue } from 'jotai'
import { SidebarInsightsMenu } from 'src/v2/components/sidebar-insights-menu'
import { PARCEL_REPORTS_MENU_ITEMS } from 'src/v2/state/owner-reveal/insights'
import { useMapExplorerNavigation } from 'src/views/map-explorer/sidebar/search/use-navigation'
import { ParcelOwners } from 'src/v2/views/owner-reveal/sidebar/parcel-owners'
import { Divider, Typography } from '@mui/material'
import { FlexRow } from 'src/components/flex-containers'
import { Briefcase } from '@emotion-icons/fa-solid/Briefcase'
import { ParcelTypeChip } from 'src/v2/components/parcel-type-chip'
import { PARCEL_TYPES_BY_MCODE_MAP } from 'src/constants/cherre-data-dictionary/zoning-colors'
import { SelectedEntityTree } from 'src/views/map-explorer/sidebar/related-results/selected-entity-tree'
import { isFeatureEnabled } from 'src/utils/feature-flag'
import { MapExplorerPanelActions } from 'src/views/map-explorer/sidebar/panels/actions'

import * as coreAtoms from 'src/v2/state/core'
import * as userListsPopoverAtoms from 'src/atoms/user-lists/user-lists-popover'
import * as userListsAtoms from 'src/atoms/user-lists'
import { ErrorBoundary } from 'src/components/errors/error-boundary'

const BGContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 180px;
  background-color: ${Colors.primaryGreen500};
  padding: 4px;
  overflow: hidden;
  border-bottom-right-radius: ${Shapes.borderRadius}px;
`

const HeaderContentWrapper = styled.div`
  padding-top: 8px;
`

export function parcelFullAddress(
  address: string | null | undefined,
  city: string | null | undefined,
  state: string,
  zip: string | null | undefined
): string {
  if (!address) {
    return '--'
  }
  return `${address}${city ? `, ${city}` : ''}, ${state}${zip ? ` ${zip}` : ''}`
}

const ParcelHeaderSubtitle = styled(FlexRow)`
  padding-top: ${Shapes.spacing[1]}px;
`

export const Container = styled.div`
  display: flex;
  flex-direction: column;
`

const ReportsWrapper = styled.div`
  padding: ${Shapes.spacing[5]}px 0;
`

type MapExplorerSelectedParcelProps = {
  showNavigationActions?: boolean
}

export const MapExplorerSelectedParcel: FC<MapExplorerSelectedParcelProps> = ({ showNavigationActions = false }) => {
  const setUserListsPopoverAnchorElement = useSetRecoilState(userListsPopoverAtoms.anchorElement)
  const [userListItem, setUserListItem] = useRecoilState(userListsAtoms.userListItem)
  const savedIn = useRecoilValue(userListsAtoms.savedIn(userListItem))
  const isSaved = useRecoilValue(userListsAtoms.isSaved(userListItem))
  const selectedEntity = useAtomValue(coreAtoms.selectedEntity)
  const { clearSelected, goBack, getBackText } = useMapExplorerNavigation()
  const isOwnershipEnabled = isFeatureEnabled('owner-reveal')

  const onClose = async () => {
    await clearSelected()
  }

  const onClickSave = (evt: React.MouseEvent<HTMLElement>) => setUserListsPopoverAnchorElement(evt.currentTarget)

  const svg = useMemo(() => {
    if (!selectedEntity || selectedEntity.type !== 'SELECTED_PARCEL') {
      return ''
    }

    const parcel = selectedEntity.data
    const geom = parcel?.geometry
    if (!geom) {
      return ''
    }

    const feature: GeoJSON.Feature = {
      type: 'Feature',
      geometry: geom,
      properties: {
        type: 'Polygon',
        fill: Colors.primaryGreen200,
        stroke: Colors.primaryGreen800,
        'stroke-width': '4px',
      },
    }

    const bounds = bbox(feature)
    const svgDimensions = {
      width: 300,
      height: 300,
    }
    const converter = new GeoJSON2SVG({
      fitTo: 'width',
      viewportSize: {
        width: svgDimensions.width,
        height: svgDimensions.height,
      },
      mapExtent: {
        left: bounds[0],
        right: bounds[2],
        top: bounds[3],
        bottom: bounds[1],
      },
    })
    const svg = converter.convert(feature, {
      // @ts-ignore
      attributes: ['properties.fill', 'properties.stroke', 'properties.stroke-width'],
    })
    const svgStr = `<svg height="180" width="350" viewBox="0 0 ${svgDimensions.width} ${svgDimensions.height}" xmlns="http://www.w3.org/2000/svg">${svg}</svg>`
    return svgStr
  }, [selectedEntity])

  useEffect(() => {
    if (isOwnershipEnabled) {
      return
    }
    void clearSelected()
  }, [isOwnershipEnabled])

  useEffect(() => {
    if (!selectedEntity || selectedEntity.type !== 'SELECTED_PARCEL' || !selectedEntity.data.address) {
      return
    }
    setUserListItem({
      id: selectedEntity.data.id,
      type: 'parcel',
      data: {
        geometry: selectedEntity.data.geometry,
        properties: {
          name: selectedEntity.data.address,
          addr: selectedEntity.data.address,
          mCode: selectedEntity.data.mCode || undefined,
          tax_assessor_id: selectedEntity.data.taxAssessorId,
        },
      },
    })
  }, [selectedEntity])

  if (!selectedEntity || selectedEntity.type !== 'SELECTED_PARCEL') {
    return null
  }

  const parcel = selectedEntity.data

  return (
    <>
      <ErrorBoundary wrap>
        <SidebarHeader
          cover={
            <>
              <HeaderCover
                badge="Parcel"
                icon={<MapSquareIcon size={8} color={Colors.bgWhite} />}
                image={<BGContainer dangerouslySetInnerHTML={{ __html: svg }} />}
                iconBackground={Colors.primaryGreen700}
                iconBorderColor={Colors.primaryGreen900}
                backText={showNavigationActions ? getBackText() : undefined}
                onBackClick={showNavigationActions ? goBack : undefined}
                onClose={showNavigationActions ? onClose : undefined}
                onClickSave={onClickSave}
                saved={isSaved}
                savedCount={savedIn.length}
              />
              <UserListsPopover />
            </>
          }
          content={
            <SidebarHeaderContent
              title={formatString(
                parcelFullAddress(parcel.address, parcel.city, parcel.state || '', parcel.zip),
                'address'
              )}
              subtitle={
                <ParcelHeaderSubtitle align="center" gap={Shapes.spacing[2]}>
                  <ParcelTypeChip type={PARCEL_TYPES_BY_MCODE_MAP[parcel.mCode || '']} />
                  <Divider orientation="vertical" flexItem />
                  <FlexRow align="center" gap={Shapes.spacing[1]}>
                    <Briefcase size={16} color={Colors.neutral300} />
                    <Typography variant="text1m" color={Colors.neutral400}>
                      {parcel.owners.length}
                    </Typography>
                  </FlexRow>
                </ParcelHeaderSubtitle>
              }>
              <HeaderContentWrapper>
                <SelectedEntityTree />
              </HeaderContentWrapper>
            </SidebarHeaderContent>
          }
        />
      </ErrorBoundary>
      <Container>
        <ErrorBoundary wrap>
          <MapExplorerPanelActions
            save={{
              onClick: onClickSave,
              isSaved,
            }}
            copy={{
              label: 'Address',
              text: formatString(
                parcelFullAddress(parcel.address, parcel.city, parcel.state || '', parcel.zip),
                'address'
              ),
            }}
            streetView
          />
        </ErrorBoundary>
        <ErrorBoundary wrap>
          <ReportsWrapper>
            <SidebarInsightsMenu title="Reports" items={PARCEL_REPORTS_MENU_ITEMS} />
          </ReportsWrapper>
        </ErrorBoundary>
        <Divider />
        <ErrorBoundary wrap>
          <ParcelOwners />
        </ErrorBoundary>
      </Container>
    </>
  )
}
